import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Popconfirm,
  InputNumber,
  Checkbox,
  Dropdown,
  Menu,
  Tag,
  Tooltip,
} from 'antd';
import { EditOutlined, MoreOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import PartnerPackageSelection from './PartnerPackageSelection';
import { toastSuccess } from '../../service/Toast';
import { InvertFillButton } from '../../components/InvertFillButton';

interface CreatePartnerPackagesProps {
  onChange: (packages: any[]) => void;
}

const CreatePartnerPackages: React.FC<CreatePartnerPackagesProps> = ({ onChange }) => {
  const [assignedPackages, setAssignedPackages] = useState<any[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPriceId, setEditingPriceId] = useState<string | null>(null);
  const [editingPrice, setEditingPrice] = useState<number | null>(null);

  // Notify parent whenever detailed list changes
  useEffect(() => {
    onChange(assignedPackages);
  }, [assignedPackages, onChange]);

  // Helper to try parsing JSON strings if necessary
  const parseValue = (val: any) => {
    if (typeof val === 'string') {
      try {
        return JSON.parse(val);
      } catch (e) {
        return val; // return as string if not valid JSON
      }
    }
    return val;
  };

  const getParameterTags = (record: any) => {
    const params: { label: string; values: string[] }[] = [];
    const pkg = record.package || record; // Handle if passing record directly or package object

    // Key fields to check
    const keysToCheck = [
      { key: 'applyingFrom', label: 'Applying From' },
      { key: 'citizenship', label: 'Citizenship' },
      { key: 'destination', label: 'Destination' },
      { key: 'country', label: 'Country' },
      { key: 'visaType', label: 'Visa Type' },
      { key: 'processingTime', label: 'Processing Time' },
    ];

    keysToCheck.forEach(({ key, label }) => {
      let val = pkg[key];
      if (!val && record[key]) val = record[key]; // detailed fallback

      if (val) {
        const parsed = parseValue(val);
        const values = Array.isArray(parsed) ? parsed : [String(parsed)];
        params.push({ label, values });
      }
    });

    return params;
  };

  const handleAdd = (data: any | any[]) => {
    const itemsToAdd = Array.isArray(data) ? data : [data];
    console.log('Adding items:', itemsToAdd);

    const newPackagesToAdd = itemsToAdd.map((item: any, index: number) => {
      // Determine Documitra Fee (default price)
      const fee = item.documitraFee !== undefined ? item.documitraFee : item.plans?.[0]?.price; // Fallback to plan price if access direct package

      return {
        ...item,
        // Ensure unique ID for local state
        id: item.id || `temp-${Date.now()}-${index}`,
        // Ensure display fields are present
        package: item.package || item, // If item is the package itself
        name: item.name || item.package?.name,
        // documitraFee
        documitraFee: fee,
        // Default customPrice to documitraFee if not explicitly set
        customPrice:
          item.customPrice !== undefined && item.customPrice !== null
            ? item.customPrice
            : Number(fee) > 0
              ? Number(fee)
              : undefined,
        // Default status
        status: 'Active',
        readyToShip: false,
      };
    });

    // Filter duplicates based on package ID validation if needed, or allow multiples?
    // For now, append.
    setAssignedPackages(prev => [...prev, ...newPackagesToAdd]);
    toastSuccess(`${newPackagesToAdd.length} package(s) added`);
  };

  const handleRemove = (id: string) => {
    setAssignedPackages(prev => prev.filter(p => p.id !== id));
    toastSuccess('Package removed');
  };

  const handleUpdatePrice = (id: string, newPrice: number | null) => {
    setAssignedPackages(prev =>
      prev.map(pkg => (pkg.id === id ? { ...pkg, customPrice: newPrice } : pkg))
    );
    setEditingPriceId(null);
    setEditingPrice(null);
    toastSuccess('Price updated');
  };

  const handleUpdateReadyToShip = (id: string, isReadyToShip: boolean) => {
    setAssignedPackages(prev =>
      prev.map(pkg => (pkg.id === id ? { ...pkg, readyToShip: isReadyToShip } : pkg))
    );
  };

  const handleUpdateStatus = (id: string, status: string) => {
    setAssignedPackages(prev =>
      prev.map(pkg => (pkg.id === id ? { ...pkg, status: status } : pkg))
    );
    toastSuccess(`Status updated to ${status}`);
  };

  const startEditingCustomPrice = (record: any) => {
    setEditingPriceId(record.id);
    setEditingPrice(record.customPrice || 0);
  };

  const cancelEditingPrice = () => {
    setEditingPriceId(null);
    setEditingPrice(null);
  };

  const getActionMenu = (record: any) => (
    <Menu>
      <Menu.Item
        key="active"
        onClick={() => handleUpdateStatus(record.id, 'Active')}
        disabled={record.status === 'Active'}
      >
        Active
      </Menu.Item>
      <Menu.Item
        key="suspend"
        onClick={() => handleUpdateStatus(record.id, 'Suspended')}
        disabled={record.status === 'Suspended'}
      >
        Suspend
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="delete" danger onClick={() => handleRemove(record.id)}>
        Remove
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: 'Service Name',
      key: 'serviceName',
      width: 250,
      render: (record: any) => (
        <span style={{ fontWeight: 500 }}>{record.name || record.package?.name || '--'}</span>
      ),
    },
    {
      title: 'Parameters',
      key: 'parameters',
      width: 150,
      render: (record: any) => {
        const params = getParameterTags(record);

        if (params.length === 0) {
          return <span style={{ color: '#8c8c8c' }}>--</span>;
        }

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {params.map((param, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip
                  title={
                    <div
                      style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '300px' }}
                    >
                      {param.values.map((v, i) => (
                        <Tag key={i} style={{ margin: 0 }}>
                          {v}
                        </Tag>
                      ))}
                    </div>
                  }
                  color="white"
                  overlayInnerStyle={{ color: 'black' }}
                >
                  <span
                    style={{
                      fontSize: '13px',
                      color: '#1890ff',
                      cursor: 'pointer',
                      borderBottom: '1px dashed #1890ff',
                    }}
                  >
                    {param.label} ({param.values.length})
                  </span>
                </Tooltip>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      title: 'Documitra Fee',
      key: 'documitraFee',
      width: 150,
      render: (record: any) => (
        <span style={{ fontWeight: 500 }}>${Number(record.documitraFee || 0).toFixed(2)}</span>
      ),
    },
    {
      title: 'Custom Price',
      key: 'customPrice',
      width: 180,
      render: (record: any) => {
        const isEditing = editingPriceId === record.id;

        if (isEditing) {
          return (
            <Space>
              <InputNumber
                value={editingPrice}
                onChange={val => setEditingPrice(val)}
                prefix="$"
                min={0}
                style={{ width: 100 }}
                autoFocus
              />
              <Button
                type="text"
                icon={<CheckOutlined />}
                onClick={() => handleUpdatePrice(record.id, editingPrice)}
                size="small"
                style={{ color: '#52c41a' }}
              />
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={cancelEditingPrice}
                size="small"
                danger
              />
            </Space>
          );
        }

        return (
          <Space>
            <span style={{ fontWeight: 500 }}>
              {record.customPrice !== undefined && record.customPrice !== null
                ? `$${Number(record.customPrice).toFixed(2)}`
                : '--'}
            </span>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => startEditingCustomPrice(record)}
              size="small"
            />
          </Space>
        );
      },
    },
    {
      title: 'Ready to Ship',
      key: 'readyToShip',
      width: 150,
      align: 'center' as const,
      render: (record: any) => (
        <Checkbox
          checked={record.readyToShip}
          onChange={e => handleUpdateReadyToShip(record.id, e.target.checked)}
        />
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (record: any) => {
        const status = record.status || 'Active';
        const color =
          status === 'Active' ? '#52c41a' : status === 'Suspended' ? '#faad14' : '#d9d9d9';
        return <span style={{ color, fontWeight: 500 }}>{status}</span>;
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      render: (record: any) => (
        <Dropdown overlay={getActionMenu(record)} trigger={['click']}>
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <div
      style={{
        marginTop: '24px',
        padding: '20px',
        background: '#fff',
        borderRadius: '8px',
        border: '1px solid #f0f0f0',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <h3 style={{ margin: 0 }}>Assign Services</h3>
        <InvertFillButton
          variant="blue"
          size="md"
          fontWeight="semibold"
          onClick={() => setModalVisible(true)}
        >
          Assign Services
        </InvertFillButton>
      </div>

      <Table
        dataSource={assignedPackages}
        columns={columns}
        pagination={false}
        rowKey="id"
        bordered
        locale={{ emptyText: 'No packages added yet. Click "Assign Services" to add.' }}
        scroll={{ x: 1200 }}
      />

      {modalVisible && (
        <PartnerPackageSelection
          partnerId=""
          onClose={() => setModalVisible(false)}
          onSuccess={() => setModalVisible(false)}
          onSelectionSubmit={handleAdd}
        />
      )}
    </div>
  );
};

export default CreatePartnerPackages;
